import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { formatPhoneNumber } from '../../../../shared/utils/format.utils';
import { formatClockTime, formatDateRange, toDisplayDate } from '../../../../shared/utils/date.utils';
import { Subject, forkJoin, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DateFormatPipe, StatusBadgePipe } from '../../../../shared/pipes';
import { FlightMapComponent } from '../../../../shared/components/flight-map/flight-map.component';
import { FieldService } from '../../../../core/data-services/field.service';
import { CustomerService } from '../../../../core/data-services/customer.service';
import { SprayRecordService } from '../../../../core/data-services/spray-record.service';
import { CertificationService } from '../../../../core/data-services/certification.service';
import { OrganizationContextService } from '../../../../core/services/organization-context.service';
import { SprayRecord } from '../../../../core/data-models/spray-record.model';
import { DroneFlightEntry } from '../../../../core/data-models/drone-flight-entry.model';
import { AppliedChemical } from '../../../../core/data-models/applied-chemical.model';
import { FlightPathData } from '../../../../core/data-models/flight-path-data.model';
import { WeatherConditions } from '../../../../core/data-models/weather-conditions.model';
import { IncidentInformation } from '../../../../core/data-models/incident-information.model';
import { SprayDetails } from '../../../../core/data-models/spray-details.model';
import { VisualObserverInfo } from '../../../../core/data-models/visual-observer-info.model';
import { SafetyComplianceInfo } from '../../../../core/data-models/safety-compliance-info.model';
import { EmergencyContactInfo } from '../../../../core/data-models/emergency-contact-info.model';
import { ApplicationTypeInfo } from '../../../../core/data-models/application-type-info.model';
import { LoadMixLocation } from '../../../../core/data-models/load-mix-location.model';
import { PPE_OPTIONS, APPLICATION_TYPE_OPTIONS } from '../../../../shared/constants/spray-record-options';
import { calculateRecordTotals } from '../../../../core/utils/spray-rate-utils';
import { DroneReport } from 'src/app/core/data-models/drone-report.model';
import { DroneReportActionResult, DroneReportService } from 'src/app/core/data-services/drone-report.service';
import { CustomClaimService } from 'src/app/core/services/custom-claim.service';
import { DroneReportItemComponent } from 'src/app/shared/components/drone-report-item/drone-report-item.component';
import { DjiFlightDataComponent } from 'src/app/shared/components/dji-flight-data/dji-flight-data.component';
import { DjiFlightComparisonComponent } from 'src/app/shared/components/dji-flight-comparison/dji-flight-comparison.component';
import { djiFlightsForDrone, unmatchedDjiFlights } from 'src/app/shared/utils/dji-flight.utils';
import { DjiService } from 'src/app/core/api-services/dji.service';
import { DjiFlight } from 'src/app/core/data-models/dji-flight.model';
import { DroneReportConfirmModalComponent } from 'src/app/shared/components/drone-report-confirm-modal/drone-report-confirm-modal.component';
import { openExternal } from '../../../../core/utils/external-link-utils';

interface Field {
  id: string;
  name: string;
  acreage: number;
  cropType: string;
  latitude: number | null;
  longitude: number | null;
  address?: string;
  customerId?: string;
}

interface Customer {
  id: string;
  name: string;
}

interface Certification {
  id: string;
  name: string;
  certNumber: string;
  state: string;
  expirationDate: string;
}

interface Drone {
  id: string;
  name: string;
  nNumber?: string;
  serialNumber: string;
}

interface Pilot {
  id: string;
  name: string;
}

@Component({
  selector: 'app-spray-record-details',
  standalone: true,
  imports: [CommonModule, 
    RouterLink, 
    DateFormatPipe, 
    StatusBadgePipe, 
    FlightMapComponent, 
    DroneReportItemComponent,
    DroneReportConfirmModalComponent,
    DjiFlightDataComponent,
    DjiFlightComparisonComponent
  ],
  templateUrl: './spray-record-details.component.html',
  styleUrls: ['./spray-record-details.component.scss']
})
export class SprayRecordDetailsComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fieldService = inject(FieldService);
  private customerService = inject(CustomerService);
  private sprayRecordService = inject(SprayRecordService);
  private certificationService = inject(CertificationService);
  private organizationContext = inject(OrganizationContextService);
  private destroy$ = new Subject<void>();
  private droneReportService = inject(DroneReportService);  
  private djiService = inject(DjiService);

  // Loading state
  isLoading = true;
  errorMessage: string | null = null;

  // Record data
  recordId: string | null = null;
  record: SprayRecord | null = null;

  // DJI flights linked to this record (empty when none; the card then renders nothing)
  djiFlights: DjiFlight[] = [];

  // Related data
  field: Field | null = null;
  customer: Customer | null = null;
  certification: Certification | null = null;
  drones: Map<string, Drone> = new Map();
  pilots: Map<string, Pilot> = new Map();

  // Coordinate display
  coordinateDisplayMode: 'decimal' | 'dms' = 'decimal';

  // Flight map modal
  showFlightMapModal = false;
  flightMapModalFlight: DroneFlightEntry | null = null;
  showAllFlights = false;

  // PPE options reference
  ppeOptions = PPE_OPTIONS;
  applicationTypeOptions = APPLICATION_TYPE_OPTIONS;

  isGenerating = signal(false);

  reportToDelete = signal<DroneReport | null>(null);
  reportToReplace = signal<DroneReport | null>(null);

  readonly recentReport = computed<DroneReport | null>(() => {
    const id = this.recordId;
    return this.droneReportService.reports().find(r => r.sprayRecordId === id) ?? null;
  });

  readonly reportError = this.droneReportService.error;

  // Drone reports are restricted to Owner, Chief Supervisor and PIC (Pilot is excluded)
  readonly canGenerateReports = computed(() => this.droneReportService.canManageReports());

  ngOnInit(): void {
    this.recordId = this.route.snapshot.paramMap.get('id');
    if (this.recordId) {
      this.loadRecord(this.recordId);
    } else {
      this.errorMessage = 'No record ID provided';
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  
  /**
   * Clear error state
   */
  clearError(): void {
    this.droneReportService.clearError();
  }

  /**
   * Retry loading reports after an error
   */
  retryLoad(): void {
    this.clearError();
    const drone = this.record?.droneFlights[0];
    if (drone)this.loadReports(drone);
  }

  private async loadRecord(id: string): Promise<void> {
    this.isLoading = true;
    this.djiFlights = [];
    this.errorMessage = null;

    try {
      // Fetch record from API
      const recordData = await this.sprayRecordService.getRecordById(id);

      if (recordData) {
        this.record = recordData;
        // Load related data
        this.loadRelatedData();
        void this.loadDjiFlights(id);
      } else {
        this.errorMessage = this.sprayRecordService.error() ?? 'Record not found';
        this.isLoading = false;
      }
    } catch (error: any) {
      console.error('Error loading spray record:', error);
      this.errorMessage = error.message || 'Failed to load spray record';
      this.isLoading = false;
    }
  }

  /**
   * DJI flight data is supplementary: a failure here must never get in the way of the record itself,
   * so it is logged and the card simply stays hidden.
   */
  private async loadDjiFlights(id: string): Promise<void> {
    try {
      const flights = await this.djiService.getSprayRecordFlights(id);
      if (this.recordId === id) {
        this.djiFlights = flights;
      }
    } catch (error) {
      console.error('Error loading DJI flight data:', error);
      if (this.recordId === id) {
        this.djiFlights = [];
      }
    }
  }

  /** Reloads the DJI flights (their detail files can arrive after the page was opened). */
  reloadDjiFlights(): void {
    if (this.recordId) {
      void this.loadDjiFlights(this.recordId);
    }
  }

  /** DJI flights matched to the aircraft of one logged drone flight. */
  djiFlightsFor(flight: DroneFlightEntry): DjiFlight[] {
    return djiFlightsForDrone(this.djiFlights, flight.droneId, flight.droneSource);
  }

  /** DJI flights linked to this record whose aircraft has no logged drone flight on it. */
  get unmatchedDjiFlights(): DjiFlight[] {
    return unmatchedDjiFlights(this.djiFlights, this.record?.droneFlights ?? []);
  }

  private async loadRelatedData(): Promise<void> {
    if (!this.record) return;

    // Try to load field from service first
    const fieldId = this.record.fieldId ?? '';
    let serviceField = this.fieldService.getFieldById(fieldId);

    // If not in service, try to load from API
    if (!serviceField) {
      try {
        await this.fieldService.loadFields();
        serviceField = this.fieldService.getFieldById(fieldId);
      } catch (error) {
        console.error('Error loading field data:', error);
      }
    }

    // Use service field if available, otherwise use minimal data from record
    this.field = serviceField ? {
      id: serviceField.id,
      name: serviceField.name ?? '',
      acreage: serviceField.acreage ?? 0,
      cropType: serviceField.cropType ?? '',
      latitude: serviceField.latitude ?? null,
      longitude: serviceField.longitude ?? null,
      address: serviceField.address ?? undefined,
      customerId: serviceField.customerId ?? undefined
    } : {
      id: fieldId,
      name: this.record.fieldName || 'Unknown Field',
      acreage: this.record.fieldAcreage || 0,
      cropType: 'Unknown',
      latitude: null,
      longitude: null,
      address: undefined,
      customerId: undefined
    };

    // Load customer if field has customerId
    if (this.field?.customerId) {
      const customerData = this.customerService.getCustomerById(this.field.customerId);
      if (customerData) {
        this.customer = {
          id: customerData.id,
          name: customerData.name ?? ''
        };
      }
    }

    // If certification snapshot exists, use it; otherwise fetch from API
    if (this.record.certificationName && this.record.certificationNumber) {
      // Use snapshot data
      this.certification = {
        id: this.record.certificationId ?? '',
        name: this.record.certificationName,
        certNumber: this.record.certificationNumber || 'N/A',
        state: this.record.certificationState || 'N/A',
        expirationDate: this.record.certificationExpiration || ''
      };
    } else if (this.record.certificationId) {
      // Snapshot missing - fetch certification data from API
      try {
        const cert = await this.certificationService.getCertification(this.record.certificationId);
        if (cert) {
          // Map certification type to display name
          const typeNames: Record<string, string> = {
            'faa-part-107': 'FAA Part 107 Remote Pilot',
            'faa-part-108': 'FAA Part 108',
            'faa-part-137': 'FAA Part 137 Agricultural Operator',
            'faa-44807': 'FAA 44807 Exemption',
            'state-applicator': 'State Applicator License',
            'medical': 'Medical Certificate',
            'insurance': 'Liability Insurance',
            'other': 'Other'
          };
          const displayName = typeNames[cert.certificationType ?? ''] || cert.certificationType || 'Certification';

          this.certification = {
            id: cert.id,
            name: displayName,
            certNumber: cert.certificateNumber || 'N/A',
            state: cert.state || 'N/A',
            expirationDate: cert.expirationDate || ''
          };
        } else {
          this.certification = {
            id: this.record.certificationId,
            name: 'Unknown Certification',
            certNumber: 'N/A',
            state: 'N/A',
            expirationDate: ''
          };
        }
      } catch (error) {
        console.error('Error fetching certification:', error);
        this.certification = {
          id: this.record.certificationId,
          name: 'Unknown Certification',
          certNumber: 'N/A',
          state: 'N/A',
          expirationDate: ''
        };
      }
    } else {
      // No certification associated
      this.certification = {
        id: '',
        name: 'No Certification',
        certNumber: 'N/A',
        state: 'N/A',
        expirationDate: ''
      };
    }

    // Build drones map from flight data (API returns drone info with flights)
    if (this.record.droneFlights) {
      this.record.droneFlights.forEach(flight => {
        if (flight.droneId && flight.droneName) {
          this.drones.set(flight.droneId, {
            id: flight.droneId,
            name: flight.droneName,
            nNumber: (flight as any).droneNNumber,
            serialNumber: (flight as any).droneSerialNumber || ''
          });
        }
      });
    }

    // Build pilots map from flight data (API returns pilot info with flights)
    if (this.record.droneFlights) {
      this.record.droneFlights.forEach(flight => {
        if (flight.pilotId && flight.pilotName) {
          this.pilots.set(flight.pilotId, {
            id: flight.pilotId,
            name: flight.pilotName
          });
        }
      });
    }
    this.loadReports(this.record.droneFlights[0]);

    this.isLoading = false;
  }

  // Navigation
  goBack(): void {
    this.router.navigate(['/logbook/spray-records']);
  }

  editRecord(): void {
    if (this.recordId) {
      this.router.navigate(['/logbook/spray-records', this.recordId, 'edit']);
    }
  }

  downloadPdf(): void {
    // Placeholder - will be implemented with backend API
    console.log('PDF download coming soon');
  }

  // Coordinate display
  toggleCoordinateDisplay(): void {
    this.coordinateDisplayMode = this.coordinateDisplayMode === 'decimal' ? 'dms' : 'decimal';
  }

  formatCoordinates(lat: number | null, lng: number | null): string {
    if (lat === null || lng === null) {
      return 'No coordinates';
    }

    if (this.coordinateDisplayMode === 'decimal') {
      return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
    } else {
      return `${this.decimalToDMS(lat, true)} ${this.decimalToDMS(lng, false)}`;
    }
  }

  private decimalToDMS(decimal: number, isLatitude: boolean): string {
    const absolute = Math.abs(decimal);
    const degrees = Math.floor(absolute);
    const minutesFloat = (absolute - degrees) * 60;
    const minutes = Math.floor(minutesFloat);
    const seconds = ((minutesFloat - minutes) * 60).toFixed(2);

    let direction: string;
    if (isLatitude) {
      direction = decimal >= 0 ? 'N' : 'S';
    } else {
      direction = decimal >= 0 ? 'E' : 'W';
    }

    return `${degrees}°${minutes}'${seconds}"${direction}`;
  }

  openInMaps(): void {
    if (this.field?.latitude && this.field?.longitude) {
      const url = `https://www.google.com/maps?q=${this.field.latitude},${this.field.longitude}`;
      window.open(url, '_blank');
    }
  }

  // Drone flight helpers
  getDroneName(droneId: string): string {
    return this.drones.get(droneId)?.name || 'Unknown Drone';
  }

  getDroneNNumber(droneId: string): string {
    return this.drones.get(droneId)?.nNumber || 'N/A';
  }

  getPilotName(pilotId: string): string {
    return this.pilots.get(pilotId)?.name || 'Unknown Pilot';
  }

  formatPhone(phone: string | undefined | null): string {
    if (!phone) return 'N/A';
    return formatPhoneNumber(phone);
  }

  /**
   * Flight times arrive as full ISO timestamps ("2026-07-29T10:00:00.000Z"),
   * not bare "HH:MM" strings. Reading the wall clock out of them literally —
   * rather than splitting on ':' or timezone-converting — is what keeps
   * "10:00 AM" from rendering as "10:00 PM".
   */
  formatTime(time: string): string {
    return formatClockTime(time);
  }

  // Flight map modal
  viewFlightPath(flight: DroneFlightEntry): void {
    this.flightMapModalFlight = flight;
    this.showAllFlights = false;
    this.showFlightMapModal = true;
  }

  viewAllFlightPaths(): void {
    this.flightMapModalFlight = null;
    this.showAllFlights = true;
    this.showFlightMapModal = true;
  }

  closeFlightMapModal(): void {
    this.showFlightMapModal = false;
    this.flightMapModalFlight = null;
    this.showAllFlights = false;
  }

  getAllFlightPaths(): FlightPathData[] {
    if (!this.record) return [];
    return this.record.droneFlights
      .filter(f => f.flightPath !== null)
      .map(f => f.flightPath!);
  }

  getFieldCenter(): { lat: number; lng: number } | null {
    if (this.field?.latitude && this.field?.longitude) {
      return { lat: this.field.latitude, lng: this.field.longitude };
    }
    return null;
  }

  // Status helpers
  getStatusText(status: string | null | undefined): string {
    switch (status) {
      case 'draft': return 'Draft';
      case 'completed': return 'Completed';
      case 'pending': return 'Pending';
      case 'cancelled': return 'Cancelled';
      default: return status ?? '';
    }
  }

  // Chemical source badge
  getSourceBadgeClass(source: string): string {
    switch (source) {
      case 'book':
        return 'bg-primary-subtle text-primary';
      case 'custom':
        return 'bg-info-subtle text-info';
      case 'template':
        return 'bg-success-subtle text-success';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  getSourceLabel(source: string): string {
    switch (source) {
      case 'book': return 'Book';
      case 'custom': return 'Custom';
      case 'template': return 'Template';
      default: return source;
    }
  }

  // Application types
  getApplicationTypeLabels(): string[] {
    if (!this.record?.applicationTypeInfo?.applicationTypes) return [];
    return this.record.applicationTypeInfo.applicationTypes.map(type => {
      const option = this.applicationTypeOptions.find(o => o.value === type);
      return option?.label || type;
    });
  }

  // PPE labels
  getPpeLabels(): string[] {
    if (!this.record?.safetyCompliance?.ppeRequired) return [];
    return this.record.safetyCompliance.ppeRequired.map(ppe => {
      const option = this.ppeOptions.find(o => o.value === ppe);
      return option?.label || ppe;
    });
  }

  // Certification expiration
  getCertificationDaysUntilExpiration(): number {
    if (!this.certification?.expirationDate) return -1; // Return -1 for missing dates
    // Calendar-date aware: an expiration of "2028-12-31" must not read as
    // Dec 30 for anyone west of UTC (same shift the display had).
    const expDate = toDisplayDate(this.certification.expirationDate);
    // Check if date is valid
    if (!expDate || isNaN(expDate.getTime())) return -1;
    const now = new Date();
    return Math.ceil((expDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  }

  isCertificationExpiringSoon(): boolean {
    const days = this.getCertificationDaysUntilExpiration();
    // Don't show warning if no expiration date set
    if (days === -1) return false;
    return days > 0 && days <= 30;
  }

  isCertificationExpired(): boolean {
    const days = this.getCertificationDaysUntilExpiration();
    // Don't show expired if no expiration date set
    if (days === -1) return false;
    return days <= 0;
  }

  // Totals calculations
  get totalAcreageSprayed(): number {
    if (!this.record) return 0;
    return this.record.droneFlights.reduce((sum, f) => sum + (f.acreageSprayed || 0), 0);
  }

  get totalChemicalUsage(): { chemicalName: string; totalAmount: string }[] {
    if (!this.record) return [];
    const { totalChemicalUsage } = calculateRecordTotals(this.record.droneFlights);
    const result: { chemicalName: string; totalAmount: string }[] = [];
    totalChemicalUsage.forEach((value) => {
      result.push({ chemicalName: value.name, totalAmount: value.totalAmount });
    });
    return result;
  }

  // Incident helpers
  get hasAnyIncidents(): boolean {
    if (!this.record?.incidentInfo) return false;
    const info = this.record.incidentInfo;
    return !!(info.takeOffOrLandingDamage || info.equipmentMalfunction || info.hasAccident);
  }

  // TrackBy functions
  trackByChemicalId(index: number, chemical: AppliedChemical): string {
    return chemical.id;
  }

  trackByFlightId(index: number, flight: DroneFlightEntry): string {
    return flight.id;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private getReportName(): string {
    const droneName = this.getDroneName(this.record?.droneFlights[0]?.droneId ?? '');
    const date = formatDateRange(this.record?.applicationDate, '', {}, false);

    return `${droneName ?? 'Unknown'} (${date ? date : 'Unknown Date'}) - Incident Report`;
  }

  //   ------ Drone Reporting ------

  private async loadReports(flight: DroneFlightEntry): Promise<void> {
    if (!this.canGenerateReports()) return;

    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) {
      console.warn('No organization ID available, cannot load Drone reports');
      return;
    }

    await this.droneReportService.loadReports(orgId, {
      droneId: flight.droneId,
      droneSource: flight.droneSource
    });
  }


  /**
  * Execute generation after confirmation
  */
  async generateReport(): Promise<void> {
    this.isGenerating.set(true);

    try {
      // Create the report request
      await this.droneReportService.startReport({
        name: this.getReportName(),
        startDate: this.record?.applicationDate ?? '',
        droneId: this.record?.droneFlights[0]?.droneId ?? '',
        droneSource: this.record?.droneFlights[0]?.droneSource ?? 'organization',
        scope: 'single_incident',
        sprayRecordId: this.record?.id ?? '',
      });
    } finally {
      this.isGenerating.set(false);
    }
  }

  /**
   * Download report PDF
   * Gets fresh pre-signed URL and opens in new tab
   */
  async downloadReport(report: DroneReport): Promise<void> {
    const url = await this.droneReportService.getDownloadUrl(report);
    if (url) {
      openExternal(url);
    }
  }

  async retryReport(report: DroneReport): Promise<void> {
    const orgId = this.organizationContext.currentOrganizationId();
    if (!orgId) return;

    if (report.status !== 'failed') return;

    await this.droneReportService.retryFailedReport(orgId, report.id);
  }

  /**
   * Delete report
   */
    readonly deleteReport = async (report: DroneReport): Promise<DroneReportActionResult> => {
      return this.droneReportService.removeReport(report);
    };

  /**
   * Replace a report
   */
  readonly replaceReport = async (report: DroneReport): Promise<DroneReportActionResult> => {
    const deleted = await this.deleteReport(report);
    if (!deleted.ok) return deleted;

    // Generation errors surface in the page-level report alert
    await this.generateReport();
    return { ok: true };
  };
}
